import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Hexagon, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

const pub = axios.create({ baseURL: API });

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pub.get(`/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      toast.success("Joined!");
      navigate(`/app/hives/${data.hive_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/invites/${token}/decline`);
      toast.success("Invitation declined");
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-500">Loading…</div>;

  if (error) {
    return (
      <Screen>
        <h1 className="font-display text-2xl font-medium">Invitation unavailable</h1>
        <p className="text-stone-600 mt-2" data-testid="invite-error">{error}</p>
        <Link to="/" className="text-amber-700 hover:underline mt-4 inline-block">← Back home</Link>
      </Screen>
    );
  }
  if (!invite) return <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-500">Loading…</div>;

  if (!user) {
    // Not signed in — redirect to login, then come back here
    const next = encodeURIComponent(`/invites/${token}`);
    return (
      <Screen>
        <h1 className="font-display text-2xl font-medium">You've been invited</h1>
        <p className="text-stone-700 mt-2">
          <strong>{invite.inviter_name}</strong> invited you to be a <strong>{invite.role}</strong> on <strong>{invite.hive_name}</strong>.
        </p>
        <p className="text-sm text-stone-500 mt-3">Sign in or create an account with <strong>{invite.invite_email}</strong> to accept.</p>
        <div className="flex gap-2 mt-5">
          <Link to={`/login?next=${next}`} className="flex-1">
            <Button className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="invite-signin-link">Sign in</Button>
          </Link>
          <Link to={`/register?email=${encodeURIComponent(invite.invite_email)}&next=${next}`} className="flex-1">
            <Button variant="outline" className="w-full rounded-full border-stone-300" data-testid="invite-signup-link">Create account</Button>
          </Link>
        </div>
      </Screen>
    );
  }

  const wrongAccount = user.email.toLowerCase() !== invite.invite_email.toLowerCase();

  return (
    <Screen>
      <div className="flex items-center gap-2 text-amber-700 mb-3">
        <UserPlus className="w-5 h-5" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Hive invitation</span>
      </div>
      <h1 className="font-display text-2xl font-medium">Join <span className="text-amber-700">{invite.hive_name}</span></h1>
      <p className="text-stone-700 mt-2">
        <strong>{invite.inviter_name}</strong> invited you as a <strong>{invite.role}</strong>.
      </p>

      {wrongAccount && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mt-4" data-testid="invite-wrong-account">
          You're signed in as <strong>{user.email}</strong> but the invite was sent to <strong>{invite.invite_email}</strong>.
        </p>
      )}

      <div className="flex gap-2 mt-5">
        <Button onClick={accept} disabled={busy || wrongAccount} className="flex-1 rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="invite-accept-button">
          <UserPlus className="w-4 h-4 mr-1.5" /> Accept
        </Button>
        <Button onClick={decline} disabled={busy} variant="outline" className="flex-1 rounded-full border-stone-300" data-testid="invite-decline-button">
          <X className="w-4 h-4 mr-1.5" /> Decline
        </Button>
      </div>
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
        </Link>
        <div className="bg-white rounded-3xl border border-stone-200 p-8" data-testid="invite-card">
          {children}
        </div>
      </div>
    </div>
  );
}
